import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { ThemingService } from 'src/app/theming.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AuthService } from 'src/app/auth.service';
import { REPORTS_ALLOWED_SHOP_IDS } from 'src/app/components/reports/reports.models';
declare var $: any;

interface SidebarSubmenuItem {
  id: string;
  labelKey: string;
  route?: string;
  icon?: string;
  disabled?: boolean;
  tooltipKey?: string;
}

interface SidebarMenuItem {
  id: string;
  labelKey: string;
  route?: string;
  permission?: string;
  children?: SidebarSubmenuItem[];
}

@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'],
})
export class SideBarComponent implements OnInit, OnDestroy {
  shopSubscription: any;
  permissions: any = {};
  hasAccess!: boolean;
  isSidebarExpanded = false;
  expandedMenuId: string | null = null;
  isNavigatingToReports = false;
  readonly reportsRoute = '/reports';
  readonly ordersMenuItem: SidebarMenuItem = {
    id: 'orders',
    labelKey: 'ORDERS',
    route: '/orders',
    permission: 'sales',
    children: [
      {
        id: 'reports',
        labelKey: 'REPORTS',
        tooltipKey: 'REPORTS',
        route: this.reportsRoute,
        icon: 'file-text',
      },
      {
        id: 'createSale',
        labelKey: 'CREATE_ORDER',
        tooltipKey: 'CREATE_ORDER',
        route: '/create-sale',
        icon: 'plus-circle',
      },
      {
        id: 'createSales',
        labelKey: 'IMPORT_ORDERS',
        tooltipKey: 'IMPORT_ORDERS',
        route: '/create-sales',
        icon: 'import-sales',
      }
    ],
  };
  readonly clientsMenuItem: SidebarMenuItem = {
    id: 'clients',
    labelKey: 'CLIENTS',
    route: '/clients',
    permission: 'clients',
    children: [
      {
        id: 'reviews',
        labelKey: 'REVIEWS',
        tooltipKey: 'REVIEWS',
        route: '/reviews',
        icon: 'star',
      },
    ],
  };
  private routerEventsSubscription?: Subscription;
  shopLogos: { white: string; large: string; small: string } = {
    white: '',
    large: '',
    small: ''
  };
  isLoading: boolean = true;
  shopName: string = '';

  constructor(
    private router: Router,
    public themingService: ThemingService,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    translate.setDefaultLang('es');
    translate.use('es');
  }

  ngOnInit(): void {
    this.authService.getUserPermissions().then((permissions) => {
      this.permissions = permissions;
      if ((this.themingService.shopId === 4 || this.themingService.shopId === 9 || this.themingService.shopId === 15 || this.themingService.shopId === 22 || this.themingService.shopId === 29 || this.themingService.shopId === 128) && this.permissions.calendar === true) {
        this.hasAccess = true;
      }else {
        this.hasAccess = false;
      }
    }).catch((error) => {
      console.error('Error al obtener permisos del usuario:', error);
    });
    this.getShopTheming();
    this.syncExpandedMenuWithRoute(this.router.url);
    this.routerEventsSubscription = this.router.events
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.isNavigatingToReports = false;
          this.syncExpandedMenuWithRoute(event.urlAfterRedirects);
          return;
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.isNavigatingToReports = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
  }

  toggleSidebar() {
    this.isSidebarExpanded = !this.isSidebarExpanded;
  }

  setSidebarExpanded(isExpanded: boolean): void {
    this.isSidebarExpanded = isExpanded;
  }

  collapseSidebarIfNoExpandedMenu(): void {
    this.isSidebarExpanded = false;
  }

  toggleSubmenu(menuId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isSidebarExpanded = true;
    this.expandedMenuId = this.expandedMenuId === menuId ? null : menuId;
  }

  isMenuExpanded(menuId: string): boolean {
    return this.expandedMenuId === menuId;
  }

  hasActiveChild(item: SidebarMenuItem): boolean {
    return this.getVisibleChildren(item).some((child) => !!child.route && !child.disabled && this.isRouteActive(child.route));
  }

  getVisibleChildren(item: SidebarMenuItem): SidebarSubmenuItem[] {
    return (item.children || []).filter((child) => child.id !== 'reports' || this.canShowReportsSubitem());
  }

  canShowReportsSubitem(): boolean {
    return this.permissions?.sales === true && REPORTS_ALLOWED_SHOP_IDS.includes(Number(this.themingService.shopId));
  }

  onReportsClick(event: Event): void {
    if (this.isNavigatingToReports) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.isRouteActive(this.reportsRoute)) {
      this.isNavigatingToReports = true;
    }
  }

  isReportChild(child: SidebarSubmenuItem): boolean {
    return child.id === 'reports';
  }

  getSubmenuId(menuId: string): string {
    return `${menuId}-submenu`;
  }

  getSubmenuToggleLabel(item: SidebarMenuItem): string {
    const actionKey = this.isMenuExpanded(item.id) ? 'COLLAPSE_SUBMENU' : 'EXPAND_SUBMENU';
    const action = this.translate.instant(actionKey);
    const label = this.translate.instant(item.labelKey);
    return `${action}: ${label}`;
  }

  private syncExpandedMenuWithRoute(url: string): void {
    if (this.hasEnabledChildRouteActive(this.ordersMenuItem, url)) {
      this.expandedMenuId = this.ordersMenuItem.id;
      return;
    }

    if (this.hasEnabledChildRouteActive(this.clientsMenuItem, url)) {
      this.expandedMenuId = this.clientsMenuItem.id;
    }
  }

  private hasEnabledChildRouteActive(item: SidebarMenuItem, url: string): boolean {
    return this.getVisibleChildren(item).some((child) => !!child.route && !child.disabled && this.isRouteActive(child.route, url));
  }

  private isRouteActive(route: string, url: string = this.router.url): boolean {
    return url === route || url.startsWith(`${route}/`);
  }

  getShopTheming() {
    //this.themingService.getThemingService().then(() => {
      const theming = this.themingService.shopTheming[0];
      if (theming) {
        this.shopLogos.white = theming.shopLogoWhite;
        this.shopLogos.large = theming.shopLogoLarge;
        this.shopLogos.small = theming.shopLogoSmall;
        this.shopName = theming.shopName;
      }
      this.shopSubscription = this.themingService.shopTheming[0].shopSubscription;
    // }).catch((error) => {
    //   console.error('Error al obtener el servicio de theming:', error);
    // }).finally(() => {
    //   this.isLoading = false; // Los datos han sido cargados
    // });
  }

  onSignOut() {
    this.authService.onSignOut().then(() => {
      this.router.navigate(['/login']);
    });
  }

  openModal() {
    $('#settingsModal').modal('show');
  }
}
